import { useEffect, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import { fetchLabelQueue } from '../lib/api';

/**
 * Small "N photos to label" badge that links to `/label` — the public
 * label-review game. Sibling of `<QueueBadge>` (which links to the
 * self-only `/review`); kept as a separate component rather than a
 * shared one with a mode flag so the two badges' polling/visibility
 * semantics can't accidentally couple.
 *
 * Polls the label queue on mount + every `pollIntervalMs` (default
 * 60 sec). Hidden when there's nothing to label (don't clutter the
 * chrome with a zero badge) or when the fetch fails (e.g. 401
 * unauthenticated — fine, badge stays hidden).
 */

interface Props {
  /** ms between polls. Default 60_000 (1 min). 0 disables polling. */
  pollIntervalMs?: number;
  /** Optional class for the wrapping <Link>. */
  className?: string;
}

export function LabelQueueBadge({ pollIntervalMs = 60_000, className }: Props) {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function poll(): Promise<void> {
      try {
        const m = await fetchLabelQueue();
        if (!cancelled) setCount(m.items.length);
      } catch {
        if (!cancelled) setCount(null);
      }
    }
    void poll();
    if (pollIntervalMs <= 0) return;
    const id = window.setInterval(() => void poll(), pollIntervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [pollIntervalMs]);

  if (count === null || count === 0) return null;

  return (
    <Link
      href="/label"
      class={['queue-badge', className].filter(Boolean).join(' ')}
      aria-label={`${count} photo${count === 1 ? '' : 's'} to label`}
    >
      <span class="queue-badge__count">{count}</span>
      <span class="queue-badge__label">
        {count === 1 ? 'photo to label' : 'photos to label'}
      </span>
    </Link>
  );
}
